/**
 * The site's rules, as pure functions over raw item rows — no React, no fetch.
 *
 * THE MODEL (vault: "Site and Presentation Mode"). A page is a dashboard with a public URL:
 *   - the Site item carries the nav (`home_page`, `header_links`, `footer_links`) and its settings
 *     in the `custom_fields.Apps` entry for `site`;
 *   - a Page item is an item with a slug whose stored View holds `displayConfig.frame` (page
 *     settings) and `displayConfig.modules`, exactly where a dashboard keeps its modules;
 *   - a module is the same object a dashboard renders, drawn as a full-width section using its
 *     `section` settings. Modules are never items; they point at items.
 *
 * ONE MODULE SHAPE. The keys here are the dashboard's own wherever one exists — a header's
 * `eyebrow`/`headline`/`subline`/`backgroundImage`/`end`, an info module's `title`/`body`/`links`,
 * a collection's `source`/`viewType`/`max` — so a Page opened inside Arcos renders as an ordinary
 * dashboard. Only what dashboards have no word for is new: `section`, `item`, `image`, `aside`,
 * `cards`, and the `form` and `html` kinds.
 */
import { parseStoredViews } from '../../utils/viewStorage.js';
import { siteBinding } from './siteSettings.js';
import { walkSource, similarFields, similarScores, rankBySimilarity, isMeaningSource, meaningScores } from '../../utils/relationshipWalk.js';
import { resolveFilterValue, applyPredicate, isDateOperator } from '../../utils/predicates.js';
import { priceBandFor } from '../../utils/priceBand.js';
import { PRICE_BAND_FIELD_KEY, PRICE_BAND_FIELD_LABEL, PRICE_BAND_WORDS } from '../../utils/priceBandField.js';

export const SITE_VIEW_ID = 'site';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (s) => UUID_RE.test(String(s || ''));

/** A row's custom_fields as an object, whatever was stored. */
export function cf(row) {
  const v = row?.custom_fields;
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

/** An ordered list of item ids from a linked-item value: ids, `{id}` objects, or a JSON string. */
export function idList(value) {
  let v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { v = v ? [v] : []; }
  }
  if (!Array.isArray(v)) return [];
  return v.map((x) => (x && typeof x === 'object' ? x.id : x)).filter((x) => typeof x === 'string' && x);
}

/**
 * The page's site View: the stored View with id `site`, else its first dashboard View. Returns
 * `{ frame, modules }` with modules in `sortOrder`, or null when the item is not a page.
 */
export function pageViewOf(row) {
  const views = parseStoredViews(cf(row).Views);
  const view = views.find((v) => v && v.id === SITE_VIEW_ID) || views.find((v) => v && v.type === 'dashboard');
  if (!view) return null;
  const cfg = view.displayConfig || {};
  const modules = (Array.isArray(cfg.modules) ? cfg.modules : [])
    .filter((m) => m && typeof m === 'object' && m.archetype)
    .map((m, i) => ({ ...m, sortOrder: typeof m.sortOrder === 'number' ? m.sortOrder : (i + 1) * 10 }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  return { viewId: view.id, frame: cfg.frame && typeof cfg.frame === 'object' ? cfg.frame : {}, modules };
}

/**
 * Every button a module shows, as `{ label, itemId? , href? }` — the info module's link shape.
 * A header's `end` button comes first; `links` follow on any kind.
 */
export function buttonsOf(module) {
  const out = [];
  // `open` rides along only where a button says it: a file link's own choice of where it opens.
  // `count` rides along for a label that says a live number: "Sync {count} events" (siteModel.statValue).
  const one = (b) => ({ label: b.label, itemId: b.itemId, href: b.href, ...(b.open ? { open: b.open } : {}),
    ...(b.count && typeof b.count === 'object' ? { count: b.count } : {}) });
  const end = module?.end;
  if (end && end.kind === 'button' && end.label) out.push(one(end));
  for (const l of Array.isArray(module?.links) ? module.links : []) {
    if (l && l.label && (l.itemId || l.href)) out.push(one(l));
  }
  return out;
}

/**
 * A module's words, read one way across kinds. A module that names an `item` takes any blank from
 * that item, so a band featuring a document reads the document and changes when it does.
 */
export function headOf(module, rowsById = new Map()) {
  const m = module || {};
  const named = m.item ? rowsById.get(m.item) : null;
  const background = m.background === 'image' || m.backgroundImage ? (m.backgroundImage || null) : null;
  const head = m.archetype === 'header'
    ? { eyebrow: m.eyebrow && m.eyebrow !== '{date}' ? m.eyebrow : null, title: m.headline, body: m.subline }
    : { eyebrow: m.eyebrow, title: m.title, body: m.archetype === 'collection' || m.archetype === 'form' ? (m.description || m.body) : m.body };
  head.background = background;
  head.image = m.image || null;
  head.aside = m.aside || null;
  head.buttons = buttonsOf(m);
  // A small pill above the words — "An Arcos Earth project", with its mark — linking where it says.
  const b = m.badge && typeof m.badge === 'object' ? m.badge : null;
  head.badge = b && typeof b.label === 'string' && b.label.trim()
    ? { label: b.label.trim(), image: typeof b.image === 'string' ? b.image : null, ...(b.itemId ? { itemId: b.itemId } : b.href ? { href: b.href } : {}) }
    : null;
  if (named) {
    head.title = head.title || named.title;
    head.body = head.body || named.description;
    if (!head.image && !head.background) head.image = named.image_cover_url || null;
  }
  for (const k of ['eyebrow', 'title', 'body']) head[k] = head[k] ? String(head[k]) : null;
  return head;
}

// ─── Collections ───────────────────────────────────────────────────────────────────────────
//
// A deliberately small reader of `collection_source`: Manual members in order, and Dynamic queries
// by type with simple filters. The app's `resolveCollection` (src/utils/resolveCollection.js) is the
// full one; it works on graph nodes, which the signed-out site does not load yet. Anything this
// reader cannot evaluate yields no rows rather than wrong rows.

function sourceOf(row) {
  let cs = row?.collection_source;
  if (typeof cs === 'string') { try { cs = JSON.parse(cs); } catch { cs = null; } }
  if (!cs || typeof cs !== 'object') return null;
  let query = cs.query;
  if (typeof query === 'string') { try { query = JSON.parse(query); } catch { query = null; } }
  return { ...cs, query: query && typeof query === 'object' ? query : null };
}

// A stored query names a column by the display name the app's filter picker shows ("Date Start"),
// and the row carries it under the column's own name.
const COLUMN_ALIASES = { 'Date Start': 'date_start', 'Date End': 'date_end', Title: 'title', Description: 'description', URL: 'url' };

function fieldValue(row, field) {
  if (field === PRICE_BAND_FIELD_KEY) return priceWord(row);
  const col = COLUMN_ALIASES[field] || field;
  if (col in (row || {})) return row[col];
  return cf(row)[field];
}

/** The row's price as the app's chip says it — "Free", "Under $25", "Over $25" — or null (src/utils/priceBand.js). */
function priceWord(row) {
  const band = priceBandFor(cf(row));
  return band ? PRICE_BAND_WORDS[band] : null;
}

// ─── Relationships ─────────────────────────────────────────────────────────────────────────
//
// A relationship the app draws from the `links` table reaches the site as a field on the rows at its
// two ends, under the key the app's own stored queries already use for it: `link:<type id>:from` on
// the source holds the targets, `link:<type id>:to` on the target holds the sources (linkFields.js).
// So a stored filter such as "events whose Dance style is in this style's tree" resolves here as it does
// in the app, a Relationship step can name `link:<id>:from`, and a filter chip can offer the values. A
// linked-item field whose answers live in `links` (`value_storage = 'link'`) is filled under its own
// key as well, so a query or a chip that names the field finds its values wherever they are stored.

const LINK_KEY_RE = /^link:([^:]+):(from|to)$/;

/**
 * `rows` with every link among them written onto both ends, as above. `links` is
 * `[{ source_id, target_id, link_type_id }]`; a link whose other end is not among `rows` is left out, so
 * nothing names an item the reader cannot see. `linkFields` is `[{ key, link_type_id }]`, the linked-item
 * fields whose values live in `links`: each is filled on the source from its type's `from` side, unless
 * the row already holds its own array for it. Returns new rows; the given ones are not changed.
 */
export function withLinkFields(rows, links, linkFields = []) {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const added = new Map();   // row id → { key: [ids] }
  const push = (id, key, other) => {
    if (!added.has(id)) added.set(id, {});
    const m = added.get(id);
    (m[key] ||= []).includes(other) || m[key].push(other);
  };
  for (const l of links || []) {
    if (!l || !l.link_type_id || !byId.has(l.source_id) || !byId.has(l.target_id) || l.source_id === l.target_id) continue;
    push(l.source_id, `link:${l.link_type_id}:from`, l.target_id);
    push(l.target_id, `link:${l.link_type_id}:to`, l.source_id);
  }
  if (!added.size) return rows;
  return rows.map((r) => {
    const m = added.get(r.id);
    if (!m) return r;
    const own = cf(r);
    const next = { ...own, ...m };
    for (const f of linkFields || []) {
      const ids = f && f.key ? m[`link:${f.link_type_id}:from`] : null;
      if (ids && !(Array.isArray(own[f.key]) && own[f.key].length)) next[f.key] = ids;
    }
    return { ...r, custom_fields: next };
  });
}

/**
 * The names a visitor reads for the relationships among `keys` (`link:<type id>:<side>`): the Link
 * Type's own words, or on its `to` side the type's reverse wording ("Sub-style of") when it has one.
 * `typesById` must carry `display` and `reverse_predicate`. Keys that name no relationship are skipped.
 */
export function linkFieldNames(keys, typesById) {
  const out = {};
  for (const k of keys || []) {
    const m = LINK_KEY_RE.exec(String(k));
    const lt = m ? typesById.get(m[1]) : null;
    const words = !lt ? null : m[2] === 'to' && lt.reverse_predicate ? lt.reverse_predicate : lt.display;
    if (words) out[k] = words.charAt(0).toUpperCase() + words.slice(1);
  }
  return out;
}

/** Every field key a site's pages name, for reading their names: filters, columns, tabs, details and fields. */
export function shownFieldKeys(modules) {
  const keys = [];
  for (const m of modules || []) {
    for (const k of ['filters', 'tableFields', 'groupings', 'metaFields', 'fields']) if (Array.isArray(m?.[k])) keys.push(...m[k]);
  }
  return keys;
}

// The links among `rows` as the app's edges (`{ source, target, displayTypeId }`), for a `$context_tree`
// token. Read back off the `link:<id>:from` fields, once per list of rows.
const edgeCache = new WeakMap();
function edgesOf(rows) {
  if (edgeCache.has(rows)) return edgeCache.get(rows);
  const edges = [];
  for (const r of rows) {
    for (const [k, v] of Object.entries(cf(r))) {
      const m = LINK_KEY_RE.exec(k);
      if (m && m[2] === 'from' && Array.isArray(v)) for (const t of v) edges.push({ source: r.id, target: t, displayTypeId: m[1] });
    }
  }
  edgeCache.set(rows, edges);
  return edges;
}

/**
 * Does `row` pass the filter `f`? `env` is `{ contextId, rows }`: the item a type's page is drawing, for
 * `$context` and `$context_tree(<link type>)`, and every row, for the tree's walk. Values go through the
 * app's own token rules (utils/predicates.js), so `$today` is the visitor's local day and `$today+7d` a
 * week on, and the date operators compare as the app does. A token the site cannot answer — a viewer's
 * own answer, say — resolves to nothing and so matches nothing.
 */
function matchesFilter(row, f, env = {}) {
  const v = fieldValue(row, f.field);
  const want = typeof f.value === 'string' && /^[$:]/.test(f.value)
    ? resolveFilterValue(f.value, { contextItemId: env.contextId || null, allEdges: env.rows ? edgesOf(env.rows) : [] })
    : f.value;
  if (isDateOperator(f.op)) return v != null && v !== '' && want != null && applyPredicate(v, f.op, want, 'date');
  const asList = (x) => (Array.isArray(x) ? x : x == null || x === '' ? [] : String(x).split(',').map((s) => s.trim()));
  const norm = (x) => String(x ?? '').trim().toLowerCase();
  switch (f.op) {
    case 'eq': case 'is': case 'equals': return norm(Array.isArray(v) ? v[0] : v) === norm(want);
    case 'neq': case 'is_not': return norm(Array.isArray(v) ? v[0] : v) !== norm(want);
    case 'has_any': { const have = asList(v).map(norm); return asList(want).map(norm).some((w) => have.includes(w)); }
    case 'is_checked': return v === true || v === 'true';
    case 'is_not_empty': return asList(v).length > 0;
    default: return false;   // an operator this reader does not know matches nothing
  }
}

/**
 * Does `row` have one of the types the query names? A name matches a type's plural display name,
 * case aside — exactly the app's rule (resolveCollection.js, `dataTypes`). Matching more here once let
 * a collection naming "Organization" fill on the site while the same collection sat empty in Arcos.
 */
function typeMatches(row, wanted, typesById) {
  if (!wanted.length) return true;
  const t = typesById.get(row.item_type_id);
  if (!t || !t.display) return false;
  return wanted.some((w) => String(w).toLowerCase() === String(t.display).toLowerCase());
}

const bySortThenTitle = (a, b) => (a.sort_order ?? 1e9) - (b.sort_order ?? 1e9) || String(a.title || '').localeCompare(String(b.title || ''));

/**
 * One step of a Relationship Source, over rows. A step naming a field follows it: the item's own value
 * when it holds one, otherwise — walking `incoming` or `both` — the rows whose same field names it. That
 * is what the app finds through the edges such a field draws, so one stored Source reads the same in both
 * places. A step naming a Link Type follows the links the payload wrote onto the rows (withLinkFields).
 */
function siteStepIds(fromId, step, byId, rows) {
  // A Link Type step, in the app's stored shape (`viaKind: 'linkType'`), follows the relationship the
  // payload wrote onto each row: outgoing reads `link:<id>:from`, incoming `link:<id>:to`, both reads both.
  if (step?.viaKind === 'linkType' && step.linkTypeId) {
    const row = byId.get(fromId);
    const dir = step.direction || 'both';
    const out = dir === 'incoming' ? [] : idList(fieldValue(row, `link:${step.linkTypeId}:from`));
    const inn = dir === 'outgoing' ? [] : idList(fieldValue(row, `link:${step.linkTypeId}:to`));
    return [...new Set([...out, ...inn])];
  }
  const via = step?.via;
  if (!via) return [];
  const own = idList(fieldValue(byId.get(fromId), via));
  if (own.length) return own;
  if ((step.direction || 'both') === 'outgoing') return [];
  return rows.filter((r) => idList(fieldValue(r, via)).includes(fromId)).map((r) => r.id);
}

/** Does a Relationship Source's Keep list keep this row? By type id, or by a type's plural or singular name. */
function keptByQuery(query, row, typesById) {
  const ids = Array.isArray(query.filterTypeIds) ? query.filterTypeIds : [];
  const names = (Array.isArray(query.filterDataTypes) ? query.filterDataTypes : []).map((n) => String(n).toLowerCase());
  if (!ids.length && !names.length) return true;
  if (ids.includes(row.item_type_id)) return true;
  const t = typesById.get(row.item_type_id);
  return !!t && names.some((n) => n === String(t.display || '').toLowerCase() || n === String(t.display_singular || '').toLowerCase());
}

/**
 * A Relationship collection's rows, walked from `contextId` — the item whose page is showing. With no
 * context there is nothing to walk from and the walk finds nothing, as in the app.
 */
function relationshipRows(collectionRow, src, rows, typesById, contextId) {
  const q = src.query || {};
  const from = !q.from || q.from === '$context' ? contextId : q.from === '$anchor' ? null : q.from;
  if (!from) return [];
  const byId = new Map(rows.map((r) => [r.id, r]));
  const home = collectionRow.primary_space_id;
  const local = rows.filter((r) => !home || !r.primary_space_id || r.primary_space_id === home);
  const walked = new Set();
  let scores = null;
  for (const source of Array.isArray(q.sources) ? q.sources : []) {
    if (isMeaningSource(source)) {
      scores = scores || new Map();
      for (const [id, n] of meaningScores(byId.get(from), fieldValue)) if (byId.has(id)) scores.set(id, (scores.get(id) || 0) + n);
      continue;
    }
    const fields = similarFields(source);
    if (fields) {
      scores = scores || new Map();
      for (const [id, n] of similarScores(byId.get(from), local, fields, fieldValue)) scores.set(id, (scores.get(id) || 0) + n);
      continue;
    }
    for (const id of walkSource(from, source, (id, step) => siteStepIds(id, step, byId, local))) walked.add(id);
  }
  const ids = new Set([...walked, ...(scores ? scores.keys() : [])]);
  ids.delete(from);
  const found = [...ids].map((id) => byId.get(id)).filter((r) => r && keptByQuery(q, r, typesById)).sort(bySortThenTitle);
  return scores ? rankBySimilarity(found, walked, scores) : found;
}

/**
 * The rows a collection holds, from the rows the reader can see. `contextId` is the item a
 * Relationship collection walks from — the item whose page the collection is shown on.
 */
export function resolveSiteCollection(collectionRow, rows, typesById = new Map(), { contextId = null } = {}) {
  const src = sourceOf(collectionRow);
  if (!src) return [];
  const byId = new Map(rows.map((r) => [r.id, r]));
  const excluded = new Set(idList(src.excluded_items));
  if ((src.kind || 'manual') === 'manual') {
    return idList(src.manual_items).map((id) => byId.get(id)).filter((r) => r && !excluded.has(r.id));
  }
  if (src.kind === 'relationship') {
    // The app's order: the walk, less exclusions, then any pinned items not already there.
    const found = relationshipRows(collectionRow, src, rows, typesById, contextId).filter((r) => !excluded.has(r.id));
    const have = new Set(found.map((r) => r.id));
    return [...found, ...idList(src.manual_items).map((id) => byId.get(id)).filter((r) => r && !have.has(r.id))];
  }
  if (src.kind !== 'query' || !src.query) return [];
  const wanted = Array.isArray(src.query.dataTypes) ? src.query.dataTypes : [];
  const filters = Array.isArray(src.query.filters) ? src.query.filters : [];
  const manual = new Set(idList(src.manual_items));
  // A query reads the collection's OWN space; another space's item joins only by being listed in its
  // manual items — the app's rule (resolveCollection.js, crossSpaceItemIdsForCollection). Without
  // this, an item of another space the site loaded for some other reason would match a local query.
  const home = collectionRow.primary_space_id;
  const local = (r) => !home || !r.primary_space_id || r.primary_space_id === home;
  const env = { contextId, rows };
  return rows
    .filter((r) => r.id !== collectionRow.id && !excluded.has(r.id))
    .filter((r) => manual.has(r.id) || (local(r) && typeMatches(r, wanted, typesById) && filters.every((f) => matchesFilter(r, f, env))))
    .sort(bySortThenTitle);
}

/**
 * `rows` in a module's order: `sortBy` is the dashboard's `{ field, direction }` (a module's
 * `source.sortBy`), read as fieldValue reads — "Date Start" is the start date. A row with no value
 * goes last either way, so an undated event never leads a date-ordered list. Without one, the
 * collection's own order stands.
 */
export function sortSiteRows(rows, sortBy) {
  const field = sortBy && typeof sortBy.field === 'string' ? sortBy.field : null;
  if (!field) return rows;
  const dir = sortBy.direction === 'desc' ? -1 : 1;
  const key = (r) => {
    const v = fieldValue(r, field);
    const x = Array.isArray(v) ? v[0] : v;
    return x == null || x === '' ? null : typeof x === 'number' ? x : String(x).toLowerCase();
  };
  return rows.map((r, i) => ({ r, i, k: key(r) })).sort((a, b) => {
    if (a.k === null || b.k === null) return a.k === b.k ? a.i - b.i : a.k === null ? 1 : -1;
    return (a.k < b.k ? -1 : a.k > b.k ? 1 : 0) * dir || a.i - b.i;
  }).map((x) => x.r);
}

// ─── A type's page ─────────────────────────────────────────────────────────────────────
//
// A site may name a Page that draws every item of a type (`detail.page_by_type`). The Page is an
// ordinary Page — its modules, its sections, its editor — read with the item as `$context`: a `fields`
// module draws the item's own fields, a Relationship collection walks from it, and `{field}` in a
// module's words is filled from it. So "More from this organization" and "Related programs" are two
// collection modules on the program page, edited like any other section.

/** The Page drawing `row`, by its type, or null when its type has none (or `row` is that Page). */
export function templatePageFor(row, settings, typesById, rowsById) {
  if (!row) return null;
  const key = typesById.get(row.item_type_id)?.key;
  const id = key ? settings?.detail?.page_by_type?.[key] : null;
  const page = id ? rowsById.get(id) : null;
  return page && page.id !== row.id && pageViewOf(page) ? page : null;
}

/** The type key a Page draws the items of, or null when it is a page in its own right. */
export function templateTypeOf(pageRow, settings) {
  if (!pageRow) return null;
  const map = settings?.detail?.page_by_type || {};
  return Object.keys(map).find((k) => map[k] === pageRow.id) || null;
}

/**
 * `text` with each `{field}` filled from the item a type's page is drawing: `{title}`, `{description}`,
 * or a field key — a linked item reads as its title, several values joined. A field the item leaves
 * empty reads as nothing, and the words around it close up.
 */
export function fillTokens(text, row, rowsById = new Map()) {
  if (typeof text !== 'string' || !text.includes('{') || !row) return text;
  return text.replace(/\{([A-Za-z0-9_ ]{1,60})\}/g, (_, key) => {
    const v = key === 'title' || key === 'description' ? row[key] : fieldValue(row, key);
    // A list whose every entry names an item the site holds is linked items, read by their titles.
    const ids = Array.isArray(v) ? idList(v) : [];
    if (ids.length && ids.length === v.length && ids.every((id) => rowsById.has(id))) return ids.map((id) => rowsById.get(id).title).filter(Boolean).join(', ');
    return valuesOf(v).join(', ');
  }).replace(/[ \t]{2,}/g, ' ').trim();
}

// ─── Present ───────────────────────────────────────────────────────────────────────────

/**
 * The order Present plays a site in: the home page, then each header link followed by its menu, then
 * the footer — every page once, and only items that are pages (they have modules to show as slides).
 */
export function presentOrder(siteRow, rowsById) {
  const s = cf(siteRow);
  const ids = [];
  const push = (id) => { if (!ids.includes(id)) ids.push(id); };
  for (const id of idList(s.home_page)) push(id);
  for (const id of idList(s.header_links)) {
    push(id);
    for (const child of idList(cf(rowsById.get(id)).menu_links)) push(child);
  }
  for (const id of idList(s.footer_links)) push(id);
  return ids.filter((id) => { const r = rowsById.get(id); return r && pageViewOf(r); });
}

// ─── Field values, for filters and item pages ─────────────────────────────────────────────

/**
 * A field's value as the words a visitor reads: a multi-select's array, a role list's `value`s
 * (`[{type, value}]`, the phones and emails shape), or one string. Empty for anything else.
 */
export function valuesOf(value) {
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((x) => (x && typeof x === 'object' ? x.value ?? x.name ?? x.label : x))
    .filter((x) => typeof x === 'string' && x.trim())
    .map((x) => x.trim());
}

/**
 * The words a visitor reads for `row`'s `field`: a linked-item value — a field or a relationship
 * (`link:<id>:from`) — as the titles of the items it names that the site holds, the derived Price
 * (`price_band`) as its band, anything else as valuesOf reads it. What filters offer and compare.
 */
export function wordsOf(row, field, rowsById = null) {
  const v = fieldValue(row, field);
  // Ids, or a list whose every entry names an item the site holds (fillTokens' test).
  const held = rowsById && Array.isArray(v) && v.length && v.every((x) => typeof x === 'string' && rowsById.has(x)) ? v : null;
  const ids = linkedIds(v) || held;
  if (ids) return rowsById ? ids.map((id) => rowsById.get(id)?.title).filter((t) => typeof t === 'string' && t.trim()) : [];
  return valuesOf(v);
}

/**
 * What a filter on `field` offers over `rows`: each value once with how many rows carry it, the
 * most common first and "Other" last, since it says least about what is behind it.
 */
export function filterChoices(rows, field, rowsById = null) {
  const counts = new Map();
  for (const r of rows) for (const v of new Set(wordsOf(r, field, rowsById))) counts.set(v, (counts.get(v) || 0) + 1);
  // Price keeps the app chip's own order — Free, Under $25, Over $25 — rather than the most common first.
  if (field === PRICE_BAND_FIELD_KEY) {
    const order = Object.values(PRICE_BAND_WORDS);
    return [...counts].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0])).map(([value, count]) => ({ value, count }));
  }
  return [...counts]
    .sort((a, b) => Number(a[0] === 'Other') - Number(b[0] === 'Other') || b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([value, count]) => ({ value, count }));
}

/** A pick as a list of values: one value, several, or none. */
const pickedValues = (v) => (Array.isArray(v) ? v : v ? [v] : []).filter((x) => typeof x === 'string' && x);

/**
 * The rows the picks keep. `picks` is `{ field: value | [values] }`: a row must carry one of the
 * values picked for a field (either country), and pass every field picked (a country AND a format). An
 * empty value or list means any.
 */
export function applyPicks(rows, picks, rowsById = null) {
  const active = Object.entries(picks || {}).map(([f, v]) => [f, pickedValues(v)]).filter(([, vs]) => vs.length);
  if (!active.length) return rows;
  return rows.filter((r) => active.every(([f, vs]) => { const have = wordsOf(r, f, rowsById); return vs.some((v) => have.includes(v)); }));
}

/**
 * What one filter's menu offers: every value the rows carry, in `filterChoices` order so the menu does
 * not reshuffle as picks change, each counted under every OTHER field's picks — "how many would I see
 * if I ticked this too". A value the other picks leave nothing of stays, with 0, so it can be unticked.
 */
export function filterMenu(rows, field, picks = {}, rowsById = null) {
  const others = Object.fromEntries(Object.entries(picks || {}).filter(([f]) => f !== field));
  const counts = new Map(filterChoices(applyPicks(rows, others, rowsById), field, rowsById).map((c) => [c.value, c.count]));
  return filterChoices(rows, field, rowsById).map((c) => ({ value: c.value, count: counts.get(c.value) || 0 }));
}

/**
 * The fields a list could filter on, for the editor to offer: keys whose values are short words —
 * a multi-select, a single select — that two or more rows share, with how many values each has.
 * Not a linked-item field (its values are ids), a web address or prose.
 */
export function filterableFields(rows) {
  const seen = new Map();   // key → Set of values
  for (const r of rows.slice(0, 500)) {
    for (const [k, v] of Object.entries(cf(r))) {
      if (k === 'Views' || k === 'Apps' || linkedIds(v)) continue;
      const vals = valuesOf(v);
      if (!vals.length || vals.some((x) => x.length > 60 || /^(https?:|mailto:)/.test(x))) continue;
      if (!seen.has(k)) seen.set(k, new Set());
      for (const x of vals) seen.get(k).add(x);
    }
  }
  return [...seen].filter(([k]) => filterChoices(rows, k).some((c) => c.count > 1))
    .map(([key, vals]) => ({ key, count: vals.size }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

/** How a filter of `count` values draws under a module's `filterStyle`: 'menu' or 'pills'. */
export function filterDrawsAs(style, count) {
  if (style === 'menus') return 'menu';
  if (style === 'auto') return count > 4 ? 'menu' : 'pills';
  return 'pills';
}

/**
 * The item ids a linked-item value holds, or null when the value is not one: a non-empty array whose
 * every entry is an id or an `{id}` object. A multi-select's words never pass, so a list of tags
 * still reads as tags.
 */
export function linkedIds(value) {
  if (!Array.isArray(value) || !value.length) return null;
  const ids = idList(value);
  return ids.length === value.length && ids.every(isUuid) ? ids : null;
}

/** Is this a picture's address? By its file ending, which is what every stored image carries. */
export const isImageUrl = (v) => typeof v === 'string' && /^https?:\/\/\S+\.(jpe?g|png|gif|webp|avif|svg)(\?\S*)?$/i.test(v);

// The site's own scaffolding links to everything it shows, through nav and menu fields; none of
// that is the item being "related" to anything.
const SCAFFOLD_TYPES = new Set(['page', 'site', 'collection']);

/**
 * The reached items that point at `row` through a linked-item field, grouped by type in the order
 * each type is first met: `[{ type, rows }]`. This is how a project's page can list the talks about it
 * when only the talk stores the link — a relationship is written once, on whichever side holds it.
 */
export function backlinksOf(row, { rows, reach, typesById }) {
  if (!row) return [];
  const groups = new Map();
  for (const r of rows) {
    if (r.id === row.id || !reach.has(r.id)) continue;
    const type = typesById.get(r.item_type_id);
    if (type && SCAFFOLD_TYPES.has(type.key)) continue;
    const points = Object.entries(cf(r)).some(([k, v]) => k !== 'Views' && (linkedIds(v) || []).includes(row.id));
    if (!points) continue;
    const key = type?.id || 'untyped';
    if (!groups.has(key)) groups.set(key, { type: type || null, rows: [] });
    groups.get(key).rows.push(r);
  }
  return [...groups.values()].map((g) => ({ ...g, rows: g.rows.sort(bySortThenTitle) }));
}

// ─── Video ─────────────────────────────────────────────────────────────────────────────────

/** "1250", "1250s" or "20m50s" as seconds; anything else as 0. */
function startSeconds(t) {
  if (!t) return 0;
  if (/^\d+$/.test(t)) return Number(t);
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(t);
  return m ? (Number(m[1]) || 0) * 3600 + (Number(m[2]) || 0) * 60 + (Number(m[3]) || 0) : 0;
}

/**
 * A YouTube address as `{ id, start }`, or null for anything else. Reads the watch, short-link,
 * embed and shorts forms, and a start time given as `t` or `start`.
 */
export function youtubeOf(url) {
  let u;
  try { u = new URL(String(url || '').trim()); } catch { return null; }
  const host = u.hostname.replace(/^(www|m)\./, '');
  let id = null;
  if (host === 'youtu.be') id = u.pathname.slice(1).split('/')[0];
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (u.pathname === '/watch') id = u.searchParams.get('v');
    else id = (/^\/(?:embed|shorts|live|v)\/([^/?#]+)/.exec(u.pathname) || [])[1] || null;
  }
  if (!id || !/^[A-Za-z0-9_-]{11}$/.test(id)) return null;
  return { id, start: startSeconds(u.searchParams.get('start') || u.searchParams.get('t')) };
}

/**
 * The video an item plays: the first YouTube address among its web address and `video_url`, else the
 * a `youtube_video_id` field, which holds the bare eleven-character id rather than an address.
 */
export function videoOf(row) {
  const bare = cf(row).youtube_video_id;
  return youtubeOf(row?.url) || youtubeOf(cf(row).video_url)
    || (typeof bare === 'string' && /^[A-Za-z0-9_-]{11}$/.test(bare.trim()) ? { id: bare.trim(), start: 0 } : youtubeOf(bare));
}

/**
 * Google's own Calendar icon, from Google's own server, for "Sync to Google Calendar". Google's brand terms
 * want the product icon used as it is, so it is never redrawn (components/SyncToCalendarButton.jsx).
 */
export const GOOGLE_CALENDAR_ICON = 'https://www.gstatic.com/images/branding/product/2x/calendar_2020q4_48dp.png';

// A neutral item mark: what stands in for a type that has no picture of its own.
const ITEM_GLYPH_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1d1d27" stroke-width="1.6"><circle cx="12" cy="12" r="8"/><circle cx="9.6" cy="9.6" r="2.2" fill="#1d1d27" stroke="none"/></svg>';

/**
 * The picture a type stands for, as an image address: its own icon (`types.svg`) when it has one, else a
 * neutral item mark. Drawn as an <img>, never inlined, so an icon's markup can run nothing on the page.
 */
export function typeGlyph(type) {
  const svg = typeof type?.svg === 'string' && /^\s*<svg[\s>]/i.test(type.svg) ? type.svg : ITEM_GLYPH_SVG;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export const youtubeThumb = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

/** The privacy-enhanced player address; the visitor's click is what loads it, so it may autoplay. */
export const youtubeEmbed = ({ id, start }) => `https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1${start ? `&start=${start}` : ''}`;

/**
 * The name a visitor reads for a field: the registry's (`names`, from the site endpoint), else the
 * key made readable — an unregistered key such as `volunteer_url` still needs a label.
 */
/**
 * The columns a table view shows, in order. `tableFields` when the module lists them; otherwise the
 * title, the first filter field (what the list is sorted into), the description and the link — what a
 * visitor scans a directory by. The title always leads, because a row without its name is unreadable.
 */
export function tableColumns(module) {
  const listed = (Array.isArray(module?.tableFields) ? module.tableFields : []).filter((k) => typeof k === 'string' && k);
  const first = Array.isArray(module?.filters) ? module.filters.find((k) => typeof k === 'string' && k) : null;
  const cols = listed.length ? listed : ['title', first, 'description', 'url'].filter(Boolean);
  return ['title', ...cols.filter((k) => k !== 'title')].filter((k, i, a) => a.indexOf(k) === i);
}

/** The views a visitor can switch between: the module's `views` that the site draws, or none below two. */
export function switchableViews(module, drawn = ['table', 'list', 'cards', 'map', 'feed', 'calendar']) {
  const views = (Array.isArray(module?.views) ? module.views : []).filter((v, i, a) => drawn.includes(v) && a.indexOf(v) === i);
  return views.length >= 2 ? views : [];
}

/**
 * The settings of a collection's stored View of `type` — its `displayConfig` — for a site drawing the
 * collection that way: the View named `viewId` when it is of that type, else the first of the type.
 * Empty when the collection has none, and the canvas draws with its defaults.
 */
export function storedViewSettings(collectionRow, type, viewId = null) {
  const views = parseStoredViews(cf(collectionRow).Views).filter((v) => v && v.type === type);
  const view = (viewId && views.find((v) => v.id === viewId)) || views[0] || null;
  return view?.displayConfig && typeof view.displayConfig === 'object' ? view.displayConfig : {};
}

export function fieldLabel(key, names = {}) {
  if (names[key]) return names[key];
  if (key === PRICE_BAND_FIELD_KEY) return PRICE_BAND_FIELD_LABEL;
  const words = String(key).replace(/_url$/, '').replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ─── Sites and addresses ───────────────────────────────────────────────────────────────────
//
// A SPACE MAY HAVE MANY SITES (decided 2026-10-09). A site is any item carrying the `site` app
// binding. The PRIMARY site is the oldest, and it alone answers at /<space>/site. Every site added
// after it answers at /<space>/site/<its slug>, or its id while it has no slug. So a space with one
// site never shows the extra segment, and adding a second site moves nothing the first one published.
// Only the sites a reader can see count: the public site orders the public Site items, the editor
// orders every Site item its admin can read. A space's own domain (api/_siteDomains.js) serves its
// primary site at the domain's root.

/** The site's base path for a space, on arcos.network — the primary site's address. */
export const siteBase = (spaceSlug) => `/${spaceSlug}/site`;

/** A space's sites, primary first: oldest by `created_at`, then by id so the order never wavers. */
export function sitesOf(rows, spaceId = null) {
  return (rows || [])
    .filter((r) => siteBinding(r.custom_fields) && (!spaceId || !r.primary_space_id || r.primary_space_id === spaceId))
    .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')) || String(a.id).localeCompare(String(b.id)));
}

/** The address segment a site answers under: none for the primary, else its slug, else its id. */
export function siteSegment(siteRow, sites) {
  if (!siteRow || !sites || !sites.length || sites[0].id === siteRow.id) return null;
  return siteRow.slug || siteRow.id;
}

/** A site's base path on arcos.network: /<space>/site for the primary, /<space>/site/<segment> for every other. */
export function siteBaseOf(spaceSlug, siteRow, sites) {
  const seg = siteSegment(siteRow, sites);
  return seg ? `${siteBase(spaceSlug)}/${encodeURIComponent(seg)}` : siteBase(spaceSlug);
}

/**
 * Which site a path inside /<space>/site names, and the segments left for that site to resolve: a
 * first segment that is another site's slug or id selects it, and anything else is the primary's.
 * A site's slug cannot equal a page's (slugs are unique within a space); the one overlap left, with
 * another space's slug, is a path itemPath never builds.
 */
export function siteForPath(segments, sites) {
  const segs = (segments || []).filter(Boolean);
  if (!sites || !sites.length) return { site: null, rest: segs };
  let first = segs[0];
  try { first = first && decodeURIComponent(first); } catch { /* a malformed segment names no site */ }
  const other = first ? sites.slice(1).find((s) => (s.slug && s.slug === first) || s.id === String(first).toLowerCase()) : null;
  return other ? { site: other, rest: segs.slice(1) } : { site: sites[0], rest: segs };
}

/**
 * The root every address of this site hangs from: `site.base` when the context gives one — '' for a
 * site served on its own domain (www.cooperationli.org/about) — else the space's path on
 * arcos.network (/cooperationli/site/about).
 */
const rootOf = (spaceSlug, site) => (site && typeof site.base === 'string' ? site.base : siteBase(spaceSlug));

/**
 * An item's path on the site. The home page is the root. An item of the site's own space is
 * `<root>/<slug or id>`; an item owned by ANOTHER space is `<root>/<owner space's slug>/<slug or id>`,
 * because a slug is unique only within its space, so a partner's could equal one of this site's. One
 * segment is therefore always this site's own; two are always someone else's.
 *
 * A Site item is linked by linking to that site — how one site of a space points at another. From a
 * site on its own domain that address is the app's (`site.appOrigin`), where the other site lives.
 *
 * `site` is `{ spaceId, slugBySpace, base?, sites?, siteId?, appOrigin? }`. Without it, or when the
 * owner's slug is unknown, an item is addressed by its id, which cannot collide.
 */
export function itemPath(spaceSlug, row, homeId, site = null) {
  const root = rootOf(spaceSlug, site);
  if (!row) return root || '/';
  const sites = site && Array.isArray(site.sites) ? site.sites : null;
  if (sites && sites.some((s) => s.id === row.id)) {
    if (site.siteId && row.id === site.siteId) return root || '/';
    const path = siteBaseOf(spaceSlug, row, sites);
    return site.appOrigin ? `${site.appOrigin}${path}` : path;
  }
  if (row.id === homeId) return root || '/';
  const ref = encodeURIComponent(row.slug || row.id);
  const foreign = !!(site && site.spaceId && row.primary_space_id && row.primary_space_id !== site.spaceId);
  if (!foreign) return `${root}/${ref}`;
  const owner = site.slugBySpace && site.slugBySpace[row.primary_space_id];
  // An owner whose slug is also one of this space's site segments would read as that site.
  const shadowed = owner && sites && sites.slice(1).some((s) => siteSegment(s, sites) === owner);
  return owner && !shadowed ? `${root}/${encodeURIComponent(owner)}/${ref}` : `${root}/${encodeURIComponent(row.id)}`;
}

const isPageOf = (row, spaceId, typesById) => !!row && typesById.get(row.item_type_id)?.key === 'page'
  && (!spaceId || !row.primary_space_id || row.primary_space_id === spaceId);

/** Where a site's reach starts: its home page and its header and footer links, in that order. */
const navSeeds = (siteRow) => {
  const s = cf(siteRow);
  return [...idList(s.home_page), ...idList(s.header_links), ...idList(s.footer_links)];
};

/** Everything reached from `seeds`: pages' menus, the items modules name, collections' members. */
function walk(seeds, { byId, rows, typesById, missing }) {
  const ids = new Set();
  const queue = [...seeds];
  const seen = new Set();
  while (queue.length) {
    const id = queue.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    const row = byId.get(id);
    if (!row) { if (missing) missing.add(id); continue; }
    ids.add(id);
    if (siteBinding(row.custom_fields)) continue;   // a link to a site is a link, not a door into it
    queue.push(...idList(cf(row).menu_links));
    const view = pageViewOf(row);
    for (const m of view ? view.modules : []) {
      if (m.item && isUuid(m.item)) queue.push(m.item);   // `$context` names no item of its own
      for (const b of [...buttonsOf(m), ...(Array.isArray(m.cards) ? m.cards.map((c) => c.link).filter(Boolean) : [])]) {
        if (b.itemId) queue.push(b.itemId);
      }
      // A section's live numbers count collections of their own; what they hold is reached too.
      for (const sid of statCollections(m)) {
        const sc = byId.get(sid);
        if (sc) for (const r of resolveSiteCollection(sc, rows, typesById)) ids.add(r.id);
        else if (missing) missing.add(sid);
      }
      const collId = m.source && m.source.collection;
      const coll = collId ? byId.get(collId) : null;
      if (collId && !coll && missing) missing.add(collId);
      if (!coll) continue;
      for (const r of resolveSiteCollection(coll, rows, typesById)) ids.add(r.id);
      if (missing) for (const mid of idList(sourceOf(coll)?.manual_items)) if (!byId.has(mid)) missing.add(mid);
    }
  }
  return ids;
}

/**
 * Every item a site reaches, and so every item with an address on it:
 *   - its navigation, each page's menu, the items its modules name, and the members of the collections
 *     they show;
 *   - on the PRIMARY site, every Page of the space a visitor can read as well (decided 2026-10-09:
 *     making a page makes it live; the navigation decides only what the header and footer list) — less
 *     the pages another site's navigation reaches and this one's does not, which are that site's.
 * Any other item never acquires a site address, so a stray public item stays off the site.
 *
 * `missing`, when given, collects every id the site refers to that `rows` does not hold — items of
 * other spaces in a collection's manual items or in the nav, which the server then fetches.
 */
export function reachableIds({ siteRow, rows, typesById, missing = null }) {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const siteSpace = siteRow && siteRow.primary_space_id;
  const sites = sitesOf(rows, siteSpace);
  const primary = !siteRow || !sites.length || sites[0].id === siteRow.id;
  let pages = [];
  if (primary) {
    const claimed = new Set();
    for (const other of sites.slice(1)) {
      for (const id of walk(navSeeds(other), { byId, rows, typesById, missing: null })) {
        if (isPageOf(byId.get(id), siteSpace, typesById)) claimed.add(id);
      }
    }
    pages = rows.filter((r) => isPageOf(r, siteSpace, typesById) && !claimed.has(r.id)).map((r) => r.id);
  }
  return walk([...navSeeds(siteRow), ...pages], { byId, rows, typesById, missing });
}

/**
 * The reached item a path names. One segment is this site's own item by slug, or any reached item by
 * id; two segments are another space's item, `<owner space's slug>/<slug or id>`.
 */
export function itemForPath(segments, { rows, reach, spaceId = null, slugBySpace = {} }) {
  const segs = (segments || []).filter(Boolean).map((x) => decodeURIComponent(x));
  if (!segs.length || segs.length > 2) return null;
  const ref = segs[segs.length - 1];
  const byId = (r) => isUuid(ref) && r.id === ref.toLowerCase();
  if (segs.length === 2) {
    const owner = Object.keys(slugBySpace || {}).find((k) => slugBySpace[k] === segs[0]);
    if (!owner) return null;
    return rows.find((r) => reach.has(r.id) && r.primary_space_id === owner && (r.slug === ref || byId(r))) || null;
  }
  const own = (r) => !spaceId || !r.primary_space_id || r.primary_space_id === spaceId;
  return rows.find((r) => reach.has(r.id) && ((own(r) && r.slug === ref) || byId(r))) || null;
}

/** One segment of a path; see itemForPath. */
export function itemForSegment(segment, ctx) {
  return itemForPath([segment], ctx);
}

/** The address a link names as `share:`, which makes it a Share button rather than a link. */
export const SHARE_HREF = 'share:';
export const isShareHref = (href) => String(href || '').trim().toLowerCase() === SHARE_HREF;

/**
 * The address a Share button hands on: the site's home. On its own domain's primary site that is the
 * domain (`ownOrigin`, from api/_siteDomains.js by way of the site's data), wherever the visitor is
 * reading it, so a share made on arcos.network still passes on www.cooperationli.org. Any other site
 * shares its home where it is being read.
 */
export function siteShareUrl({ ownOrigin = null, isPrimary = true, base = '', locationOrigin = '' } = {}) {
  if (ownOrigin && isPrimary) return `${String(ownOrigin).replace(/\/$/, '')}/`;
  return `${locationOrigin}${base || '/'}`;
}

/**
 * What kind of file an address names, read from its path: `pdf`, `image`, or null for anything else
 * (a web page, a download the viewer cannot draw). Storage addresses end in the file's own extension,
 * which is what makes this enough.
 */
export function fileKindOf(href) {
  let path;
  try { path = new URL(String(href || ''), 'https://x.invalid').pathname.toLowerCase(); } catch { return null; }
  if (/\.pdf$/.test(path)) return 'pdf';
  if (/\.(png|jpe?g|webp|gif|avif|svg)$/.test(path)) return 'image';
  return null;
}

/**
 * Whether a link to a file opens over the page. The link's own `open` wins (`viewer` or `tab`); else
 * the site's `chrome.file_links`. Only a file the viewer can draw (fileKindOf) ever opens there.
 */
function opensInViewer(link, href, ctx) {
  const choice = link.open === 'viewer' || link.open === 'tab' ? link.open : ctx.settings?.chrome?.file_links;
  return choice === 'viewer' ? fileKindOf(href) : null;
}

/**
 * Where a button goes. Returns `{ href, external }` for a link, `{ form }` for a form, `{ share }`
 * for a Share button and `{ file }` for a file opened over the page:
 *   - `itemId`            → that item's page on the site
 *   - `href` "#<module>"  → a section on this page
 *   - `href` "/form/<id>" → an Arcos form, opened in place
 *   - `href` "share:"     → the visitor's share sheet with the site's address, or the address copied
 *   - a PDF or a picture  → over the page, when the link or the site says so (`file` is its kind)
 *   - any other `href`    → an external page, in a new tab
 */
export function resolveLink(link, ctx) {
  const { spaceSlug, rowsById, homeId } = ctx;
  if (!link) return { href: rootOf(spaceSlug, ctx) || '/' };
  if (link.itemId) return { href: itemPath(spaceSlug, rowsById.get(link.itemId) || { id: link.itemId }, homeId, ctx) };
  const href = String(link.href || '');
  // Its href is the address being shared, so a modified click, or a page without JavaScript, opens it.
  if (isShareHref(href)) return { href: ctx.shareUrl || rootOf(spaceSlug, ctx) || '/', share: true };
  const form = href.match(/^\/form\/([^/?#]+)/);
  // A form lives in the Arcos app. On a site's own domain its path would name nothing there, so it
  // goes to the app's origin (`ctx.appOrigin`) instead.
  if (form) return { href: ctx.appOrigin ? `${ctx.appOrigin}${href}` : href, form: form[1] };
  const file = opensInViewer(link, href, ctx);
  if (file) return { href, external: true, file };
  if (href.startsWith('#') || href.startsWith('/')) return { href };
  return { href, external: true };
}

// ─── Feed, details and Browse ─────────────────────────────────────────────────────────────

const DATE_KEYS = new Set(['date_start', 'Date Start', 'date_end', 'Date End']);
const localDayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// A date-only value ("2026-10-18") names a day, not an instant at UTC midnight, so it is read as that day.
const asDate = (v) => {
  if (v == null || v === '') return null;
  const s = String(v);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};
const isDayOnly = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v ?? ''));

/** "8:00 PM" for an instant on the visitor's clock, or null for a value that names only a day. */
export function timeOf(value, locale) {
  const d = asDate(value);
  if (!d || isDayOnly(value)) return null;
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(d);
}

/** "Tue, Oct 14 · 8:00 PM" — a start as a visitor reads it beside a title. */
export function whenOf(value, locale) {
  const d = asDate(value);
  if (!d) return null;
  const day = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' }).format(d);
  const t = timeOf(value, locale);
  return t ? `${day} · ${t}` : day;
}

/** "13th", "1st", "22nd": a day of the month as English writes it; other languages get the bare number. */
function ordinalDay(n, locale) {
  const lang = String(locale || (typeof navigator !== 'undefined' ? navigator.language : 'en') || 'en').toLowerCase();
  if (!lang.startsWith('en')) return String(n);
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
  return `${n}${suffix}`;
}

/**
 * `rows` by the day each starts, in the visitor's own time: `[{ key, label, date, rows }]`, the days in
 * order and each day's rows by start time, then one group of rows with no start, "Date to be announced".
 * `label` names the day — "Today", "Tomorrow", else its weekday — and `date` is the short date beside it,
 * "Oct 15th", so a heading reads "Today · Oct 13th" or "Thursday · Oct 15th". The group with no start has
 * no `label`.
 */
export function feedDays(rows, { now = new Date(), locale } = {}) {
  const today = localDayKey(now);
  const tomorrow = localDayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  const days = new Map();
  const undated = [];
  for (const r of rows) {
    const d = asDate(r.date_start);
    if (!d) { undated.push(r); continue; }
    const key = localDayKey(d);
    if (!days.has(key)) days.set(key, { key, d, rows: [] });
    days.get(key).rows.push(r);
  }
  const out = [...days.values()].sort((a, b) => a.key.localeCompare(b.key)).map(({ key, d, rows: rs }) => ({
    key,
    label: key === today ? 'Today' : key === tomorrow ? 'Tomorrow' : new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(d),
    date: `${new Intl.DateTimeFormat(locale, { month: 'short' }).format(d)} ${ordinalDay(d.getDate(), locale)}`,
    rows: rs.map((r, i) => ({ r, i, t: asDate(r.date_start).getTime() })).sort((a, b) => a.t - b.t || a.i - b.i).map((x) => x.r),
  }));
  if (undated.length) out.push({ key: 'undated', label: null, date: 'Date to be announced', rows: undated });
  return out;
}

/**
 * The details a module shows under a row's title (`metaFields`), each as one short string: a start or end
 * date written out, a relationship as the titles it names, anything else as its words. Empties drop out.
 */
export function metaWords(row, fields, rowsById = null, locale) {
  const out = [];
  for (const f of Array.isArray(fields) ? fields : []) {
    if (typeof f !== 'string' || !f) continue;
    const text = DATE_KEYS.has(f) ? whenOf(fieldValue(row, f), locale) : wordsOf(row, f, rowsById).join(', ');
    if (text) out.push(text);
  }
  return out;
}

/**
 * What Browse offers for `field` over `rows`: each value once with how many rows carry it, as
 * filterChoices orders them, and — when the value is an item the site holds, a style or a venue —
 * that item, so its card can show the item's picture and open its page.
 */
export function browseValues(rows, field, rowsById = null) {
  const items = new Map();
  if (rowsById) {
    for (const r of rows) {
      const v = fieldValue(r, field);
      const ids = linkedIds(v) || (Array.isArray(v) && v.every((x) => typeof x === 'string' && rowsById.has(x)) ? v : []);
      for (const id of ids) { const it = rowsById.get(id); if (it?.title && !items.has(it.title)) items.set(it.title, it); }
    }
  }
  return filterChoices(rows, field, rowsById).map((c) => ({ ...c, item: items.get(c.value) || null }));
}

/**
 * One live number in a section's `stats` strip: `{ collection, filters, distinct, label, one, itemId | href }`.
 * It counts the rows the collection holds that pass `filters` (a stored query's shape: "today" is
 * `Date Start` `is_on` `$today`), or, with `distinct`, the different values of that field across them —
 * the styles the upcoming events are in. Null when the collection is not one the site holds.
 */
export function statValue(stat, { rows, rowsById, typesById }) {
  const coll = stat && typeof stat.collection === 'string' ? rowsById.get(stat.collection) : null;
  if (!coll || !coll.collection_source) return null;
  const env = { rows };
  const filters = (Array.isArray(stat.filters) ? stat.filters : []).filter((f) => f && f.field && f.op);
  const found = resolveSiteCollection(coll, rows, typesById).filter((r) => filters.every((f) => matchesFilter(r, f, env)));
  if (typeof stat.distinct !== 'string' || !stat.distinct) return found.length;
  const seen = new Set();
  for (const r of found) for (const w of wordsOf(r, stat.distinct, rowsById)) seen.add(w);
  return seen.size;
}

/**
 * The collections a module reads besides its `source`: the ones its `stats` count and the one its live
 * picture draws (`media.collection`), so the payload carries them and what they hold.
 */
export const statCollections = (module) => [
  ...(Array.isArray(module?.stats) ? module.stats : []).map((s) => s && s.collection),
  ...buttonsOf(module).map((b) => b.count && b.count.collection),
  module?.media && typeof module.media === 'object' ? module.media.collection : null,
].filter((id) => typeof id === 'string' && isUuid(id));

// ─── A live week ──────────────────────────────────────────────────────────────────────────

const hourOf = (d) => d.getHours() + d.getMinutes() / 60;

/**
 * What a live week picture (`media: { kind: 'week', collection }`) draws from `rows`: the next `days`
 * days from today, the evening hours the events fall in (at most `span` of them), and the events that
 * fit — each day's in start order, never two overlapping in one column, up to `max` in all, taken in
 * turn across the days so the week fills evenly. An event with no end, or one ending as it starts, is
 * drawn an hour and a half long. `{ days: [{ key, name, num, today }], hours: [h…], events: [{ d, from,
 * to, title, style, id }] }`, the events in the order they arrive.
 */
export function weekArt(rows, { days = 4, max = 7, span = 5, now = new Date(), rowsById = null, styleField = null, locale } = {}) {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayList = Array.from({ length: days }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { key: localDayKey(d), name: new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d).toUpperCase(), num: d.getDate(), today: i === 0 };
  });
  const index = new Map(dayList.map((d, i) => [d.key, i]));
  const byDay = dayList.map(() => []);
  for (const r of rows) {
    const s = asDate(r.date_start);
    if (!s || isDayOnly(r.date_start)) continue;
    const d = index.get(localDayKey(s));
    if (d == null) continue;
    const e = asDate(r.date_end);
    const from = hourOf(s);
    const to = e && e > s && localDayKey(e) === localDayKey(s) ? hourOf(e) : Math.min(from + 1.5, 24);
    if (now && d === 0 && to <= hourOf(now)) continue;   // already over today
    byDay[d].push({ d, from, to, title: r.title || '', id: r.id, style: styleField ? (wordsOf(r, styleField, rowsById)[0] || '') : '' });
  }
  // The window: from the hour the earliest kept event starts, `span` hours on.
  const firsts = byDay.flat().map((e) => e.from);
  if (!firsts.length) return { days: dayList, hours: [], events: [] };
  // A week of evenings reads as one: start at the earliest evening event (5 PM on) when there is one.
  const evening = firsts.filter((f) => f >= 17);
  const h0 = Math.floor(Math.min(...(evening.length ? evening : firsts)));
  const h1 = Math.min(h0 + span, 24);
  const fits = (e) => e.from >= h0 && e.from < h1 - 0.5;
  const lanes = byDay.map((list) => list.filter(fits).sort((a, b) => a.from - b.from));
  const chosen = dayList.map(() => []);
  let added = true;
  while (added && chosen.flat().length < max) {
    added = false;
    for (let d = 0; d < dayList.length && chosen.flat().length < max; d++) {
      const next = lanes[d].find((e) => !chosen[d].includes(e) && chosen[d].every((c) => e.from >= c.to || e.to <= c.from));
      if (next) { chosen[d].push(next); added = true; }
    }
  }
  const events = chosen.flat().map((e) => ({ ...e, to: Math.min(e.to, h1) }))
    .sort((a, b) => a.d - b.d || a.from - b.from);
  // The hours end where the last event does, three at the least, so no empty evening trails below.
  const end = Math.min(h1, Math.max(h0 + 3, Math.ceil(Math.max(...events.map((e) => e.to)))));
  return { days: dayList, hours: Array.from({ length: end - h0 + 1 }, (_, i) => h0 + i), events };
}

/**
 * The number a card shows (`cardCount`): how many rows the named collection holds walked from the card's
 * item, or the card's item's own when it is itself a collection (`$self`). Null when nothing is set or
 * the collection is not one the site holds.
 */
export function cardCountOf(row, cardCount, { rows, rowsById, typesById }) {
  const id = cardCount && typeof cardCount.collection === 'string' ? cardCount.collection : null;
  if (!id || !row) return null;
  const coll = id === '$self' ? row : rowsById.get(id);
  if (!coll || !coll.collection_source) return null;
  const found = resolveSiteCollection(coll, rows, typesById, { contextId: row.id });
  const filters = (Array.isArray(cardCount.filters) ? cardCount.filters : []).filter((f) => f && f.field && f.op);
  const env = { contextId: row.id, rows };
  return filters.length ? found.filter((r) => filters.every((f) => matchesFilter(r, f, env))).length : found.length;
}
